/**
 * Feature E07-F05 — Evidence Drawer pure model/logic (web side).
 *
 * Framework-free, side-effect-free helpers ported 1:1 from the prototype
 * `../EER app/prototype/assets/research.proto.js` (openEvidence L1095-1131,
 * verdictChip L840, gradeLabel L1133, the trust block L1125-1127). Keeping the
 * render rules here makes the verdict→colour palette (T02), the weighted-
 * contribution / division-note subheader formatting (T04) and the trust-banner
 * tone styling (T06) unit-testable without a DOM — exactly like
 * researchReviewModel.ts.
 *
 * The wire shapes + the canonical criterion order/labels/weights come from
 * `@eer/shared` (research-evidence.ts) — single source of truth, never
 * re-declared here. This module only adds the *presentation* (colour classes,
 * formatted strings) the React `EvidenceDrawer` paints.
 */
import type {
  EvidenceCriterionRow,
  EvidenceDrawer,
  RequestMoreInfoResponse,
  TrustTone,
} from '@eer/shared';

// ---------------------------------------------------------------------------
// T02 — verdict → colour palette (pass green / partial amber / fail red / unclear gray)
// ---------------------------------------------------------------------------

export type DrawerVerdict = EvidenceCriterionRow['verdict'];

export interface VerdictStyle {
  /** Badge classes (bg + text + ring) for the verdict pill. */
  readonly badgeClass: string;
  /** Left-border accent for the criterion card (border-l-4 tone). */
  readonly accentClass: string;
  /** Left-border colour of the verbatim-quote blockquote (same tone as the badge). */
  readonly quoteBorderClass: string;
  /** Icon (Font Awesome) for the verdict. */
  readonly icon: string;
}

/**
 * Verdict colour map (prototype verdictChip L840 + the evidence card accents).
 * pass → green, partial → amber, fail → red, unclear → slate. Drives the badge
 * pill AND the criterion card's left accent so the drawer is colour-coded by
 * verdict at a glance (T02 acceptance).
 */
const VERDICT_STYLE_MAP: Record<DrawerVerdict, VerdictStyle> = {
  pass: {
    badgeClass: 'bg-green-100 text-green-700 ring-green-300',
    accentClass: 'border-green-400',
    quoteBorderClass: 'border-green-500',
    icon: 'fa-circle-check',
  },
  partial: {
    badgeClass: 'bg-amber-100 text-amber-700 ring-amber-300',
    accentClass: 'border-amber-400',
    quoteBorderClass: 'border-amber-500',
    icon: 'fa-circle-half-stroke',
  },
  fail: {
    badgeClass: 'bg-red-100 text-red-600 ring-red-300',
    accentClass: 'border-red-400',
    quoteBorderClass: 'border-red-500',
    icon: 'fa-circle-xmark',
  },
  unclear: {
    badgeClass: 'bg-slate-100 text-slate-500 ring-slate-300',
    accentClass: 'border-slate-300',
    quoteBorderClass: 'border-slate-400',
    icon: 'fa-circle-question',
  },
};

/** Verdict style for one criterion row; unknown verdict falls back to `unclear`. */
export function verdictStyle(verdict: DrawerVerdict | undefined): VerdictStyle {
  return VERDICT_STYLE_MAP[verdict ?? 'unclear'] ?? VERDICT_STYLE_MAP.unclear;
}

// ---------------------------------------------------------------------------
// T06 — trust banner tone → colour palette (verified green / needs-human amber)
// ---------------------------------------------------------------------------

export interface TrustStyle {
  /** Banner container classes (bg + text + border). */
  readonly containerClass: string;
  /** Leading icon (Font Awesome). */
  readonly icon: string;
  /** Short heading shown bold before the message. */
  readonly heading: string;
}

const TRUST_STYLE_MAP: Record<TrustTone, TrustStyle> = {
  verified: {
    containerClass: 'bg-green-50 border-green-200 text-green-800',
    icon: 'fa-shield-check',
    heading: 'Citation-verified',
  },
  'needs-human': {
    containerClass: 'bg-amber-50 border-amber-200 text-amber-800',
    icon: 'fa-triangle-exclamation',
    heading: 'Needs human review',
  },
  // E07-F04-T11 — neutral (slate), never green: nothing has checked this row yet.
  'not-checked': {
    containerClass: 'bg-slate-50 border-slate-200 text-slate-700',
    icon: 'fa-minus',
    heading: 'Not checked',
  },
};

/** Trust-banner style by tone (T06: verified green / needs-human amber). */
export function trustStyle(tone: TrustTone): TrustStyle {
  return TRUST_STYLE_MAP[tone] ?? TRUST_STYLE_MAP['needs-human'];
}

// ---------------------------------------------------------------------------
// T04 — weighted-contribution formatting (weight × match-factor)
// ---------------------------------------------------------------------------

/**
 * Format the weighted contribution of one criterion as "+N pts (W% × M)" —
 * weight% × match-factor (pass 1.0 / partial 0.5 / fail 0.0). The number is the
 * row's `contribution` (already weight×matchFactor); we surface the maths so the
 * specialist sees *why* the score is what it is (T04). The match-factor is shown
 * as a one-decimal multiplier (×1.0 / ×0.5 / ×0).
 */
export function formatContribution(row: EvidenceCriterionRow): string {
  const m = row.matchFactor;
  const mult = m === 1 ? '1.0' : m === 0.5 ? '0.5' : m === 0 ? '0' : String(m);
  return `+${row.contribution} pts (${row.weight}% × ${mult})`;
}

/** "of the active weight profile" caption for one criterion's weight (T04). */
export function formatWeight(row: EvidenceCriterionRow): string {
  return `${row.weight}% weight`;
}

// ---------------------------------------------------------------------------
// E07-F11-T05 — Jev confidence + source pages
// ---------------------------------------------------------------------------

/**
 * Jev's probability as a two-decimal confidence ("0.91"); "—" when Jev gave none. Mirrors the
 * explanation's «уверенность 0,91» (two decimals) in the drawer's English chrome.
 */
export function formatConfidence(p: number | null | undefined): string {
  if (typeof p !== 'number' || !Number.isFinite(p)) return '—';
  return (Math.round(p * 100) / 100).toFixed(2);
}

/** Short, readable form of a page address for a link caption (host + path, no scheme). */
export function pageCaption(url: string): string {
  const s = url
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '');
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

/** Only http(s) addresses get an "Open page" link (never `javascript:` from stored data). */
export function isOpenableUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim());
}

// ---------------------------------------------------------------------------
// T04 — drawer subheader (resolvedEntity · gradeLabel · score)
// ---------------------------------------------------------------------------

/**
 * Build the drawer subheader pieces (prototype openEvidence subheader,
 * "<resolvedEntity> · <gradeLabel> · score <n>"). Omits the score piece when the
 * row is unscored (a manual row before verification). Returns the segments so the
 * React view can interleave them with the division/portfolio note (T04).
 */
export function subheaderSegments(
  drawer: Pick<EvidenceDrawer, 'resolvedEntity' | 'gradeLabel' | 'score'>,
): string[] {
  const segs: string[] = [drawer.resolvedEntity, drawer.gradeLabel];
  if (drawer.score != null) segs.push(`score ${drawer.score}`);
  return segs;
}

/**
 * Sanity flag for the contribution-total vs the stored score (T04 acceptance #4):
 * Σ(contribution) should be within `tolerance` (default 1.0 pt for rounding) of
 * the row's `score`. Returns true when they agree (or the row is unscored). The
 * view shows an amber "computed Σ differs from stored score" hint when false —
 * never silently hides the mismatch.
 */
export function contributionMatchesScore(
  drawer: Pick<EvidenceDrawer, 'contributionTotal' | 'score'>,
  tolerance = 1.0,
): boolean {
  if (drawer.score == null) return true;
  return Math.abs(drawer.contributionTotal - drawer.score) <= tolerance;
}

// ---------------------------------------------------------------------------
// E07-F05-T07 — "Request more info" outcome → toast message
// ---------------------------------------------------------------------------

export interface RequestMoreInfoMessage {
  /** `success` only when a new confirmation was found or the row is already verified. */
  readonly tone: 'success' | 'error';
  readonly text: string;
}

/**
 * Map the API outcome of "Request more info" to a distinct, honest toast. The
 * key rule (E07-F05-T07): a check that RAN but found nothing new (`not_found`) is
 * NOT a success and must never read as "Already fully verified" — the researcher
 * would otherwise assume the company was confirmed. Falls back to the legacy
 * `degraded`/`upgraded` flags when `outcome` is absent (older API build).
 */
export function requestMoreInfoMessage(
  res: Pick<RequestMoreInfoResponse, 'upgraded' | 'degraded' | 'drawer'> & {
    outcome?: RequestMoreInfoResponse['outcome'];
  },
): RequestMoreInfoMessage {
  const outcome =
    res.outcome ?? (res.degraded ? 'degraded' : res.upgraded ? 'upgraded' : 'not_found');
  switch (outcome) {
    case 'degraded':
      return {
        tone: 'error',
        // E12-F02-T05: degraded = no AI model configured (not a missing search
        // source). The e2e degraded filter keys on "No AI model is configured".
        text: 'No AI model is configured — could not deepen the evidence. Ask an admin to set one up in Admin AI settings (Settings → Model Matrix).',
      };
    case 'upgraded': {
      const label =
        res.drawer.criteria.find((c) => c.key === res.upgraded)?.label ?? res.upgraded ?? '';
      return {
        tone: 'success',
        text: `New confirmation found: upgraded “${label}” to a cited PASS — needs-human cleared.`,
      };
    }
    case 'already_verified':
      return {
        tone: 'success',
        text: 'Already fully verified — every criterion is a cited PASS, nothing to check.',
      };
    case 'not_found':
    default:
      return {
        tone: 'error',
        text: 'No new evidence found — the deeper check found no citable confirmation. The company is NOT verified; review it manually.',
      };
  }
}
